import { TestBlock, toSnakeCase } from "@/src/modeler/polar";
import type { Fact, FactArg } from "@/src/modeler/polar";
import { useEffect } from "react";
import {
  AttributePrimitive,
  RelationPrimitive,
  RolePrimitive,
} from "./Primitives";
import Spinner from "../Spinner";
import { HiOutlineArrowLeft, HiOutlineArrowNarrowRight } from "react-icons/hi";
import Button from "../Button";
import { HiMiniXMark, HiOutlinePlus } from "react-icons/hi2";
import { EXAMPLE_USER_NAMES, deleteIdx, updateIdx } from "@/src/workbenchLogic";
import { State } from "@/src/modeler/state";
import { isEqual } from "lodash";

const FactArg: React.FC<{ arg: FactArg }> = ({ arg }) => {
  if (typeof arg === "object") {
    return (
      <>
        {arg.tag} <span className="font-semibold">{arg.id}</span>
      </>
    );
  } else {
    return <span className="font-semibold">{arg}</span>;
  }
};

const Selector: React.FC<{
  values: string[];
  value: string;
  setValue: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}> = ({ values, value, setValue, onFocus, onBlur }) => (
  <select
    value={value}
    onChange={(e) => setValue(e.target.value)}
    className="py-0 pl-2 pr-8 font-semibold rounded-lg border-gray-200"
    onFocus={onFocus}
    onBlur={onBlur}
  >
    {values.map((v) => (
      <option key={v} value={v}>
        {v}
      </option>
    ))}
  </select>
);

const EXAMPLE_RESOURCE_IDS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

function resourceRoles(state: State, resourceName: string): string[] {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return [];
  return resource.roles;
}

function grantableResourceNames(state: State): string[] {
  return state.resources.filter((r) => r.isGrantedAccessTo).map((r) => r.name);
}

function resourcePermissions(state: State, resourceName: string): string[] {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return [];
  return resource.permissions;
}

function resourceNames(state: State): string[] {
  return state.resources.map((r) => r.name);
}

function resourcesWithRelations(state: State): string[] {
  return state.resources
    .filter((r) => r.relations.length > 0)
    .map((r) => r.name);
}

function resourcesRelatedTo(state: State, resourceName: string): string[] {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return [];
  return resource.relations.map((r) => r.resource);
}

function resourcesWithAttributes(state: State): string[] {
  return state.resources
    .filter(
      (r) =>
        r.sharing.canShareWithParent.length > 0 ||
        r.sharing.canSharePublic !== null
    )
    .map((r) => r.name);
}

function resourceAttributes(state: State, resourceName: string): string[] {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return [];
  const attributes = [];
  if (resource.sharing.canShareWithParent.length > 0) {
    attributes.push("is_shared_with_parent");
  }
  if (resource.sharing.canSharePublic !== null) {
    attributes.push("is_public");
  }
  return attributes;
}

const Fact: React.FC<{
  state: State;
  fact: Fact;
  setFact: (fact: Fact) => void;
  testStatus: TestStatus;
  setTestStatus: (testStatus: TestStatus) => void;
}> = ({ state, fact, setFact, testStatus, setTestStatus }) => {
  switch (fact.name) {
    case "has_role": {
      const [actor, role, resource] = fact.args;
      if (
        typeof actor !== "object" ||
        typeof role !== "string" ||
        typeof resource !== "object"
      ) {
        throw new Error("Invalid fact");
      }
      return (
        <>
          User{" "}
          <Selector
            value={actor.id}
            values={EXAMPLE_USER_NAMES}
            setValue={(id) => {
              setFact({
                name: "has_role",
                args: [{ tag: "User", id }, role, resource],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag: "User", id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: actor });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, actor)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          has the <RolePrimitive />{" "}
          <Selector
            value={role}
            values={resourceRoles(state, resource.tag)}
            setValue={(role) => {
              setFact({
                name: "has_role",
                args: [actor, role, resource],
              });
            }}
          />{" "}
          on{" "}
          <Selector
            value={resource.tag}
            values={grantableResourceNames(state)}
            setValue={(tag) => {
              setFact({
                name: "has_role",
                args: [actor, role, { tag, id: resource.id }],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag, id: resource.id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: resource });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, resource)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          <Selector
            value={resource.id}
            values={EXAMPLE_RESOURCE_IDS}
            setValue={(id) => {
              setFact({
                name: "has_role",
                args: [actor, role, { tag: resource.tag, id }],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag: resource.tag, id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: resource });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, resource)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />
          .
        </>
      );
    }
    case "has_relation": {
      const [child, relation, parent] = fact.args;
      if (
        typeof child !== "object" ||
        typeof relation !== "string" ||
        typeof parent !== "object"
      ) {
        throw new Error("Invalid fact");
      }
      return (
        <>
          <Selector
            value={child.tag}
            values={resourcesWithRelations(state)}
            setValue={(tag) => {
              setFact({
                name: "has_relation",
                args: [{ tag, id: child.id }, relation, parent],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag, id: child.id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: child });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, child)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          <Selector
            value={child.id}
            values={EXAMPLE_RESOURCE_IDS}
            setValue={(id) => {
              setFact({
                name: "has_relation",
                args: [{ tag: child.tag, id }, relation, parent],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag: child.tag, id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: child });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, child)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          has a <RelationPrimitive /> to{" "}
          <Selector
            value={parent.tag}
            values={resourcesRelatedTo(state, child.tag)}
            setValue={(tag) => {
              setFact({
                name: "has_relation",
                args: [child, toSnakeCase(tag), { tag, id: parent.id }],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag, id: parent.id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: parent });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, parent)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          <Selector
            value={parent.id}
            values={EXAMPLE_RESOURCE_IDS}
            setValue={(id) => {
              setFact({
                name: "has_relation",
                args: [child, relation, { tag: parent.tag, id }],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag: parent.tag, id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: parent });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, parent)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />
          .
        </>
      );
    }
    // attribute
    default: {
      const [resource] = fact.args;
      if (typeof resource !== "object") {
        throw new Error("Invalid fact");
      }
      return (
        <>
          <Selector
            value={resource.tag}
            values={resourcesWithAttributes(state)}
            setValue={(tag) => {
              setFact({
                name: fact.name,
                args: [{ tag, id: resource.id }],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag, id: resource.id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: resource });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, resource)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          <Selector
            value={resource.id}
            values={EXAMPLE_RESOURCE_IDS}
            setValue={(id) => {
              setFact({
                name: fact.name,
                args: [{ tag: resource.tag, id }],
              });
              setTestStatus({
                ...testStatus,
                highlightedObject: { tag: resource.tag, id },
              });
            }}
            onFocus={() => {
              setTestStatus({ ...testStatus, highlightedObject: resource });
            }}
            onBlur={() => {
              if (isEqual(testStatus.highlightedObject, resource)) {
                setTestStatus({ ...testStatus, highlightedObject: null });
              }
            }}
          />{" "}
          has the <AttributePrimitive />{" "}
          <Selector
            value={fact.name}
            values={resourceAttributes(state, resource.tag)}
            setValue={(name) => {
              setFact({
                name,
                args: [resource],
              });
            }}
          />
        </>
      );
    }
  }
};

const Assertion: React.FC<{
  state: State;
  assertion: Fact;
  setAssertion: (assertion: Fact) => void;
  testStatus: TestStatus;
  setTestStatus: (testStatus: TestStatus) => void;
}> = ({ state, assertion, setAssertion, testStatus, setTestStatus }) => {
  const [actor, action, resource] = assertion.args;
  if (
    assertion.name !== "allow" ||
    typeof actor !== "object" ||
    typeof action !== "string" ||
    typeof resource !== "object"
  ) {
    throw new Error("Invalid assertion");
  }
  return (
    <>
      Can User{" "}
      <Selector
        value={actor.id}
        values={EXAMPLE_USER_NAMES}
        setValue={(id) => {
          setAssertion({
            name: "allow",
            args: [{ tag: "User", id }, action, resource],
          });
          setTestStatus({
            ...testStatus,
            highlightedObject: { tag: "User", id },
          });
        }}
        onFocus={() => {
          setTestStatus({ ...testStatus, highlightedObject: actor });
        }}
        onBlur={() => {
          if (isEqual(testStatus.highlightedObject, actor)) {
            setTestStatus({ ...testStatus, highlightedObject: null });
          }
        }}
      />{" "}
      <Selector
        value={action}
        values={resourcePermissions(state, resource.tag)}
        setValue={(action) => {
          setAssertion({
            name: "allow",
            args: [actor, action, resource],
          });
        }}
      />{" "}
      <Selector
        value={resource.tag}
        values={resourceNames(state)}
        setValue={(tag) => {
          setAssertion({
            name: "allow",
            args: [actor, action, { tag, id: resource.id }],
          });
          setTestStatus({
            ...testStatus,
            highlightedObject: { tag, id: resource.id },
          });
        }}
        onFocus={() => {
          setTestStatus({ ...testStatus, highlightedObject: resource });
        }}
        onBlur={() => {
          if (isEqual(testStatus.highlightedObject, resource)) {
            setTestStatus({ ...testStatus, highlightedObject: null });
          }
        }}
      />{" "}
      <Selector
        value={resource.id}
        values={EXAMPLE_RESOURCE_IDS}
        setValue={(id) => {
          setAssertion({
            name: "allow",
            args: [actor, action, { tag: resource.tag, id }],
          });
          setTestStatus({
            ...testStatus,
            highlightedObject: { tag: resource.tag, id },
          });
        }}
        onFocus={() => {
          setTestStatus({ ...testStatus, highlightedObject: resource });
        }}
        onBlur={() => {
          if (isEqual(testStatus.highlightedObject, resource)) {
            setTestStatus({ ...testStatus, highlightedObject: null });
          }
        }}
      />
      ?
    </>
  );
};

const AssertionResult: React.FC<{ result: boolean | null }> = ({ result }) =>
  result === null ? (
    <Spinner />
  ) : result ? (
    <div className="font-bold">Yes</div>
  ) : (
    <div className="font-bold">No</div>
  );

export type TestStatus = {
  results: (boolean | null)[];
  highlightedObject: FactArg | null;
  highlightedFact: Fact | null;
};

export const EMPTY_TEST_STATUS: TestStatus = {
  results: [],
  highlightedObject: null,
  highlightedFact: null,
};

const ConcreteInteractiveExample: React.FC<{
  state: State;
  test: TestBlock;
  setTest: (test: TestBlock) => void;
  testStatus: TestStatus;
  setTestStatus: (testStatus: TestStatus) => void;
  track: (event: string, properties: { [key: string]: unknown }) => void;
  goBack: () => void;
}> = ({
  state,
  test: { name, setup, assertions },
  setTest,
  testStatus,
  setTestStatus,
  track,
  goBack,
}) => {
  useEffect(() => {
    setTestStatus({ ...testStatus, results: assertions.map(() => null) });
    track("Modeler - Example - Run Test", {
      setup: setup.length,
      assertions: assertions.length,
    });
    /* TODO: port to new LSP functionality
    const timeout = setTimeout(() => {
      setTestStatus({
        ...testStatus,
        results: assertions.map((assertion) =>
          runTest(
            stateToPolar(state),
            setup.map(factToWasm),
            factToWasm(assertion.fact)
          )
        ),
      });
    }, 100);
    return () => clearTimeout(timeout); */
  }, [setup, assertions]);

  const grantableResource = grantableResourceNames(state)[0];
  const relatableResource = resourcesWithRelations(state)[0];
  const attributeResource = resourcesWithAttributes(state)[0];

  const addRole = grantableResource
    ? () => {
        const fact = {
          name: "has_role",
          args: [
            { tag: "User", id: "alice" },
            resourceRoles(state, grantableResource)[0],
            { tag: grantableResource, id: "0" },
          ],
        };
        setTest({ name, setup: [...setup, fact], assertions });
      }
    : undefined;
  const addRelation = relatableResource
    ? () => {
        const parent = resourcesRelatedTo(state, relatableResource)[0];
        const fact = {
          name: "has_relation",
          args: [
            { tag: relatableResource, id: "0" },
            toSnakeCase(parent),
            { tag: parent, id: "1" },
          ],
        };
        setTest({ name, setup: [...setup, fact], assertions });
      }
    : undefined;
  const addAttribute = attributeResource
    ? () => {
        const fact = {
          name: resourceAttributes(state, attributeResource)[0],
          args: [{ tag: attributeResource, id: "0" }],
        };
        setTest({ name, setup: [...setup, fact], assertions });
      }
    : undefined;
  const addOutput = () => {
    const fact = {
      name: "allow",
      args: [
        { tag: "User", id: "alice" },
        resourcePermissions(state, grantableResource)[0],
        { tag: grantableResource, id: "0" },
      ],
    };
    setTest({
      name,
      setup,
      assertions: [...assertions, { fact, expected: true }],
    });
  };

  return (
    <div>
      <div className="flex space-x-4 items-center mb-4">
        <Button icon={<HiOutlineArrowLeft />} onClick={goBack} small>
          Back
        </Button>
        <div className="text-lg font-bold">Interactive Example: {name}</div>
      </div>
      <div className="border rounded-lg mb-4 p-4">
        <div className="text-lg font-bold">Input</div>
        <div
          onMouseOut={() => {
            setTestStatus({ ...testStatus, highlightedFact: null });
          }}
        >
          {setup.map((fact, i) => (
            <div
              className="transition-colors bg-white hover:bg-gray-100 group/delete p-2 rounded flex items-center space-x-2"
              key={i}
              onMouseOver={() => {
                setTestStatus({ ...testStatus, highlightedFact: fact });
              }}
            >
              <div>
                <Fact
                  state={state}
                  fact={fact}
                  setFact={(newFact) =>
                    setTest({
                      name,
                      setup: updateIdx(setup, i, newFact),
                      assertions,
                    })
                  }
                  testStatus={testStatus}
                  setTestStatus={setTestStatus}
                />
              </div>
              <div className="grow" />
              <Button
                small
                theme="delete"
                className="opacity-0 group-hover/delete:opacity-100"
                onClick={() => {
                  setTest({ name, setup: deleteIdx(setup, i), assertions });
                }}
              >
                <HiMiniXMark />
              </Button>
            </div>
          ))}
        </div>
        <div className="text-center space-x-4">
          <Button
            small
            icon={<HiOutlinePlus />}
            disabled={!addRole}
            onClick={addRole}
          >
            <span>
              Add <RolePrimitive />
            </span>
          </Button>
          <Button
            small
            icon={<HiOutlinePlus />}
            disabled={!addRelation}
            onClick={addRelation}
          >
            <span>
              Add <RelationPrimitive />
            </span>
          </Button>
          <Button
            small
            icon={<HiOutlinePlus />}
            disabled={!addAttribute}
            onClick={addAttribute}
          >
            <span>
              Add <AttributePrimitive />
            </span>
          </Button>
        </div>
      </div>
      <div className="border rounded-lg p-4">
        <div className="text-lg font-bold">Output</div>
        <div
          onMouseOut={() => {
            setTestStatus({ ...testStatus, highlightedFact: null });
          }}
        >
          {assertions.map((assertion, i) => (
            <div
              className="transition-colors bg-white hover:bg-gray-100 group/delete p-2 rounded flex items-center space-x-2"
              key={i}
              onMouseOver={() => {
                setTestStatus({
                  ...testStatus,
                  highlightedFact: assertion.fact,
                });
              }}
            >
              <div>
                <Assertion
                  state={state}
                  assertion={assertion.fact}
                  setAssertion={(newAssertion) =>
                    setTest({
                      name,
                      setup,
                      assertions: updateIdx(assertions, i, {
                        expected: assertion.expected,
                        fact: newAssertion,
                      }),
                    })
                  }
                  testStatus={testStatus}
                  setTestStatus={setTestStatus}
                />
              </div>
              <HiOutlineArrowNarrowRight />
              <AssertionResult result={testStatus.results[i]} />
              <div className="grow" />
              <Button
                small
                theme="delete"
                className="opacity-0 group-hover/delete:opacity-100"
                onClick={() => {
                  setTest({
                    name,
                    setup,
                    assertions: deleteIdx(assertions, i),
                  });
                }}
              >
                <HiMiniXMark />
              </Button>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Button small icon={<HiOutlinePlus />} onClick={addOutput}>
            Add output
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConcreteInteractiveExample;
